import { Alert,Box,Button,CircularProgress,Container,Typography } from '@mui/material';
import React,{ useCallback } from 'react';
import { useTelegramAuth } from '../../contexts/TelegramAuthContext';
import AchievementsSystem from './components/AchievementsSystem';
import AllahNamesProgress from './components/AllahNamesProgress';
import ErrorBoundary from './components/ErrorBoundary';
import Leaderboard from './components/Leaderboard';
import StepRenderer from './components/LessonSteps/StepRenderer';
import LearningHome from './components/LearningHome';
import TermSearch from './components/TermSearch';
import LetterSelector from './components/LetterSelector';
import NavigationMenu from './components/NavigationMenu';
import Profile from './components/Profile';
import QuizSystem from './components/QuizSystem';
import Settings from './components/Settings';
import TermAnnotatedContent from './components/TermAnnotatedContent';
import { useAchievements } from './hooks/useAchievements';
import { useArabicLessonData } from './hooks/useArabicLessonData';
import { useLessonState } from './hooks/useLessonState';
// Импортируем компоненты для включения в сборку
import AyahSearchStep from './components/LessonSteps/AyahSearchStep';
import RecognitionStep from './components/LessonSteps/RecognitionStep';
import TajweedStep from './components/LessonSteps/TajweedStep';

// Экспортируем компоненты для включения в сборку
export { AyahSearchStep,TajweedStep,RecognitionStep };

const ArabicLessons: React.FC = React.memo(() => {
  const { token, isLoading: authLoading, error: authError, isAuthenticated } = useTelegramAuth();
  const lessonState = useLessonState();
  const {
    loading,
    error,
    loadLessonData,
    loadLetterProgress,
    lesson,
    saveLessonProgress,
    clearData,clearError
  } = useArabicLessonData(token);

  const {
    userStats,
    error: achievementsError
  } = useAchievements(token);

  const [activeSection,setActiveSection]=React.useState('home');
  const [progressError,setProgressError]=React.useState('');
  const [progressRevision,setProgressRevision]=React.useState(0);
  const [completionNotice,setCompletionNotice]=React.useState('');
  const lessonView=React.useRef(0);
  const mounted=React.useRef(true);
  const currentToken=React.useRef(token);currentToken.current=token;
  const selectionPending=React.useRef(false);
  const completionPending = React.useRef<object | null>(null);
  const viewAtRender=lessonView.current;
  const visibleStep=lessonState.computed.currentStep;
  const stepMarker=React.useMemo(()=>({view:viewAtRender,step:visibleStep,index:lessonState.state.currentStepIndex,token}),
    [viewAtRender,visibleStep,lessonState.state.currentStepIndex,token]);
  const committedStep=React.useRef<object|null>(null);
  const transitionPending=React.useRef(false);
  React.useLayoutEffect(()=>{
    committedStep.current=stepMarker;transitionPending.current=false;
    return()=>{if(committedStep.current===stepMarker)committedStep.current=null;};
  },[stepMarker]);
  const invalidateLessonView=useCallback(()=>{lessonView.current++;},[]);
  React.useEffect(()=>{
    mounted.current=true;completionPending.current=null;lessonState.actions.resetLesson();setCompletionNotice('');
    return()=>{mounted.current=false;invalidateLessonView();};
  },[token,invalidateLessonView,lessonState.actions]);
  const handleNavigate=useCallback((section:string)=>setActiveSection(section),[]);
  // Мемоизируем обработчик выбора буквы
  const handleLetterSelect = useCallback(async (letter: string) => {
    if(selectionPending.current)return;selectionPending.current=true;
    try {
      lessonState.actions.setLoading(true);
      lessonState.actions.setError(null);

      // Загружаем данные урока
      const lessonData = await loadLessonData(letter);
      if (!lessonData) throw new Error('Материал урока недоступен.');
      if (lessonData.steps) {
        lessonState.actions.setSteps(lessonData.steps);
      }
      lessonState.actions.setLetter(letter);

      // Обновляем прогресс буквы
      // Selection alone does not persist or complete progress.

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка загрузки урока';
      lessonState.actions.setError(errorMessage);
    } finally {
      selectionPending.current=false;lessonState.actions.setLoading(false);
    }
  }, [loadLessonData, lessonState.actions]);

  // The final step becomes completed only after the server confirms persistence.
  const handleNextStep = useCallback(async (score?: number) => {
    if(!visibleStep||viewAtRender!==lessonView.current||currentToken.current!==token||committedStep.current!==stepMarker)return;
    if (!lessonState.computed.isLastStep) {
      if(transitionPending.current)return;
      transitionPending.current=true;lessonState.actions.nextStep(score);return;
    }
    if (completionPending.current) return;

    if (lessonState.computed.isLastStep && lesson) {
      const operation = {};
      const view = lessonView.current;
      completionPending.current = operation;
      try {
        const result = await saveLessonProgress(lesson.id, {status: 'completed', step_results: {}});
        if (!result.is_completed) throw new Error('Завершение не подтверждено.');
        if (!mounted.current || currentToken.current !== token) return;
        if (view !== lessonView.current) { setProgressRevision(v=>v+1); return; }
        lessonState.actions.nextStep(score);
        lessonState.actions.completeLesson();
        if (lessonState.state.selectedLetter) lessonState.actions.updateLetterProgress(lessonState.state.selectedLetter, 'completed');
      } catch {
        if (!mounted.current || currentToken.current !== token) return;
        if (view === lessonView.current) lessonState.actions.setError('Не удалось сохранить завершение урока. Повторите попытку.');
        else {
          setCompletionNotice('Результат сохранения предыдущего урока не подтверждён. Проверьте прогресс и при необходимости повторите действие.');
          setProgressRevision(v=>v+1);
        }
      } finally {
        if (completionPending.current === operation) completionPending.current = null;
      }
    }
  }, [lessonState.actions, lessonState.computed.isLastStep, lessonState.state.selectedLetter, lesson, saveLessonProgress, token,visibleStep,viewAtRender,stepMarker]);

  // Мемоизируем обработчик возврата к выбору буквы
  const handleBackToLetters = useCallback(() => {
    lessonView.current++;completionPending.current=null;
    clearData();lessonState.actions.resetLesson();
    setActiveSection('lessons'); // Возвращаемся на главную страницу
  }, [lessonState.actions,clearData]);

  // Обработчик ошибок для ErrorBoundary
  const handleError = useCallback((error: Error, errorInfo: any) => {
    console.error('Ошибка в ArabicLessons:', error, errorInfo);
  }, []);

  React.useEffect(()=>{
    let active=true;setProgressError('');
    void loadLetterProgress().then(progress=>{if(active)lessonState.actions.replaceLetterProgress(progress);})
      .catch(()=>{if(active)setProgressError('Прогресс букв недоступен. Статусы не подтверждены.');});
    return()=>{active=false;};
  },[loadLetterProgress,lessonState.actions,progressRevision]);

  // Мемоизируем содержимое компонента
  const content = (() => {
    // Ждём завершения авторизации
    if (authLoading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>Авторизация...</Typography>
        </Box>
      );
    }

    // Показываем ошибку авторизации
    if (authError) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          {authError}
        </Alert>
      );
    }

    // Проверяем что пользователь авторизован
    if (!isAuthenticated) {
      return (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Для доступа к урокам необходима авторизация через Telegram
        </Alert>
      );
    }

    if (loading || lessonState.state.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      );
    }

    if (error || lessonState.state.error) {
      return <Box><Alert severity="error">{error||lessonState.state.error}</Alert>
        {lessonState.computed.currentStep&&<Button onClick={()=>{clearError();lessonState.actions.setError(null);}}>Вернуться к текущему шагу</Button>}
        <Button onClick={handleBackToLetters}>К выбору буквы</Button></Box>;
    }
    if(lessonState.state.isCompleted){return <Box><Alert severity="success">Завершение урока сохранено сервером как ваша отметка о прохождении.</Alert>
      <Typography>Оценка навыка и баллы за этот урок не подтверждены.</Typography><Button onClick={handleBackToLetters}>К выбору буквы</Button></Box>;}

    if (lessonState.computed.currentStep) {
      return (
        <StepRenderer
          step={lessonState.computed.currentStep}
          isLastStep={lessonState.computed.isLastStep}
          token={token}
          onNext={handleNextStep}
          onBackToLetters={handleBackToLetters}
          lessonId={lesson?.id}
        />
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <NavigationMenu
          token={token}
          onNavigate={handleNavigate}
          activeSection={activeSection}
          userStats={{
            lessons_completed: userStats?.lessons_completed || 0,
            days_streak: userStats?.days_streak || 0,
            terms_mastered: userStats?.terms_mastered || 0,
            letters_learned: userStats?.letters_learned || 0,
            time_spent: userStats?.time_spent || 0
          }}
        />

        <Container maxWidth="lg" sx={{ flexGrow: 1, py: 3 }}>
          {activeSection === 'home' ? (
            <LearningHome onNavigate={handleNavigate} stats={userStats}/>
          ) : activeSection === 'search' ? (
            <TermSearch token={token}/>
          ) : activeSection === 'lessons' ? (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Уроки арабского языка
              </Typography>
              {progressError?<Box><Alert severity="error">{progressError}</Alert><Button onClick={()=>setProgressRevision(v=>v+1)}>Загрузить прогресс букв</Button></Box>:<LetterSelector
                selectedLetter={lessonState.state.selectedLetter}
                onLetterSelect={handleLetterSelect}
                letterProgress={lessonState.state.letterProgress}
              />}
            </Box>
          ) : activeSection === 'quiz' ? (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Квизы и тесты
              </Typography>
              <QuizSystem token={token} />
            </Box>
          ) : activeSection === 'achievements' ? (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Достижения
              </Typography>
              <AchievementsSystem token={token} />
            </Box>
          ) : activeSection === 'leaderboard' ? (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Лидерборд
              </Typography>
              <Leaderboard token={token} />
            </Box>
          ) : activeSection === 'allah-names' ? (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Имена Аллаха
              </Typography>
              <AllahNamesProgress token={token} />
            </Box>
          ) : activeSection === 'terms' ? (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Исламские термины
              </Typography>
              <TermAnnotatedContent token={token} />
            </Box>
          ) : activeSection === 'profile' ? (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Профиль пользователя
              </Typography>
              <Profile token={token} onClose={() => setActiveSection('home')} />
            </Box>
          ) : activeSection === 'settings' ? (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Настройки
              </Typography>
              <Settings token={token} onClose={() => setActiveSection('home')} />
            </Box>
          ) : (
            <Box>
              <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                {activeSection}
              </Typography>
              <Alert severity="info">
                Раздел "{activeSection}" находится в разработке
              </Alert>
            </Box>
          )}
        </Container>

      </Box>
    );
  })();

  return (
    <ErrorBoundary onError={handleError}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <h1>Арабские уроки</h1>
          <p>Изучайте арабские буквы и доступные материалы</p>
        </Box>

        {achievementsError && <Alert severity="error">{achievementsError}</Alert>}
        {completionNotice && <Alert severity="warning" onClose={()=>setCompletionNotice('')}>{completionNotice}</Alert>}
        {content}
      </Container>
    </ErrorBoundary>
  );
});

ArabicLessons.displayName = 'ArabicLessons';

export default ArabicLessons;
